import { Link } from "react-router-dom";
import { Beaker, Send } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

const columns = [
  {
    title: "Products",
    links: [
      { to: "/catalog", label: "Full Catalog" },
      { to: "/catalog", label: "Recovery" },
      { to: "/catalog", label: "Metabolic" },
      { to: "/catalog", label: "Cognitive" },
    ],
  },
  {
    title: "Research",
    links: [
      { to: "/research", label: "Methodology" },
      { to: "/quality", label: "Documentation" },
      { to: "/quality", label: "COA Lookup" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/research", label: "About Peps4Pets" },
      { to: "/contact", label: "Contact" },
      { to: "/faq", label: "FAQ" },
    ],
  },
  {
    title: "Support",
    links: [
      { to: "/contact", label: "Research Desk" },
      { to: "/faq", label: "Help Center" },
      { to: "/portal", label: "Order Tracking" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/faq", label: "Terms of Use" },
      { to: "/faq", label: "Privacy" },
      { to: "/faq", label: "Research Disclaimer" },
    ],
  },
  {
    title: "Portal",
    links: [
      { to: "/login", label: "Sign In" },
      { to: "/login", label: "Create Account" },
      { to: "/portal", label: "Dashboard" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("Subscribed. Watch for the next batch drop.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    }
  };

  return (
    <footer data-testid="site-footer" className="mt-24 border-t border-slate-200 bg-slate-50">
      {/* Newsletter row */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="eyebrow mb-3">Batch Drops</div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
              Get COA notifications the moment new batches release.
            </h3>
            <p className="mt-2 text-slate-600 text-sm max-w-lg">
              One email per new batch. Documentation attached. No marketing filler.
            </p>
          </div>
          <form onSubmit={submit} className="flex gap-2" data-testid="newsletter-form">
            <Input
              data-testid="newsletter-email"
              type="email"
              required
              placeholder="research@lab.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 rounded-md border-slate-300"
            />
            <Button
              data-testid="newsletter-submit"
              type="submit"
              className="h-12 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
            >
              <Send className="w-4 h-4 mr-2" strokeWidth={1.75} /> Subscribe
            </Button>
          </form>
        </div>
      </div>

      {/* Columns */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-10">
        <div className="col-span-2 lg:col-span-2">
          <div className="flex items-center gap-2">
            <img src="/logo.webp" alt="" className="h-11 w-11 object-contain"/>
            <div className="font-display text-lg font-semibold tracking-tight text-[#0e2f6b]">
              Peps<span className="text-blue-500">4</span>Pets
            </div>
          </div>
          <p className="mt-4 max-w-sm text-sm text-slate-600 leading-relaxed">
            Independent veterinary peptide reference. Third-party tested, USA-sourced. For licensed research
            professionals.
          </p>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-400">
            Research Only · Not for Human Consumption
          </p>
        </div>

        {columns.map((col) => (
          <div key={col.title}>
            <div className="eyebrow mb-4">{col.title}</div>
            <ul className="space-y-2 text-sm text-slate-600">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-blue-700 transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <div className="text-xs text-slate-500">© {new Date().getFullYear()} Peps 4 Pets · All rights reserved</div>
          <div className="text-xs text-slate-400 font-mono">v1.1 · COA-verified · 21+</div>
        </div>
      </div>
    </footer>
  );
}
